import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { movies } from "@/mocks/movies";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function Detail() {
  const { id = "" } = useParams();
  const [shareMsg, setShareMsg] = useState("");
  const movie = movies.find((m) => m.id === id);

  useDocumentMeta({
    title: movie ? `${movie.title} - 影视详情 - 八戒影视` : "影视详情 - 八戒影视",
    description: movie
      ? `${movie.title}（${movie.year}）${movie.type}，由${movie.director}执导，${movie.actors.join("、")}主演。${movie.summary}`
      : "八戒影视影视详情页，提供影片基本信息、剧情简介与相关推荐。",
    keywords: movie
      ? `${movie.title},${movie.type},${movie.genres.join(",")},影视详情,八戒影视`
      : "影视详情,八戒影视",
  });

  const related = useMemo(() => {
    if (!movie) return [];
    const sameGenreOrActor = movies.filter(
      (m) =>
        m.id !== movie.id &&
        (m.genres.some((g) => movie.genres.includes(g)) || m.actors.some((a) => movie.actors.includes(a)))
    );
    const others = movies.filter((m) => m.id !== movie.id && !sameGenreOrActor.includes(m));
    return [...sameGenreOrActor, ...others].slice(0, 4);
  }, [movie]);

  const handleShare = (label: string) => {
    setShareMsg(`已选择「${label}」——演示功能，暂不支持真实分享。`);
    window.setTimeout(() => setShareMsg(""), 2600);
  };

  if (!movie) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="flex flex-col items-center text-center">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-400 mb-4">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="text-xl font-bold text-foreground-900">没有找到该影视作品</h1>
          <p className="text-sm text-foreground-500 mt-2">该作品可能已下架，或链接地址有误。</p>
          <Link
            to="/"
            className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const paragraphs = [
    movie.summary,
    `《${movie.title}》是一部${movie.year}年上映的${movie.country}${movie.type}，隶属于${movie.genres.join("、")}类型。影片由${movie.director}担任导演，${movie.actors.join("、")}等演员领衔出演，在叙事节奏与影像质感上都呈现出鲜明的个人风格。`,
    `在剧情层面，作品围绕主要人物的选择与命运展开，通过层层递进的冲突把观众带入故事之中。无论是人物关系的铺陈，还是关键情节的转折，都保持了较高的完成度，也因此获得了不少观众的好评。`,
    `如果你喜欢${movie.genres.join("、")}这一类题材，这部作品值得加入片单。你也可以在本站继续浏览同类型或同主演的其他影视作品，发现更多适合你的观影选择。`,
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: `${movie.type}分类`, to: `/category/${movie.type}` },
          { label: movie.title },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* 主内容 */}
        <div className="lg:col-span-2">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="w-40 sm:w-48 shrink-0 mx-auto sm:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} - ${movie.type}详情 - 八戒影视`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{movie.title}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm text-foreground-600">
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-accent-50 text-accent-700">
                  <i className="ri-star-fill"></i>
                  <strong>{movie.rating.toFixed(1)}</strong>
                  <span className="text-accent-600/70">分</span>
                </span>
                <span>{movie.year}年</span>
                <span>{movie.country}</span>
                <span className="px-2 py-0.5 rounded bg-primary-50 text-primary-700">{movie.type}</span>
              </div>

              <dl className="mt-5 space-y-2.5 text-sm">
                <div className="flex gap-3">
                  <dt className="text-foreground-400 w-16 shrink-0">导演</dt>
                  <dd className="text-foreground-800">{movie.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-foreground-400 w-16 shrink-0">主演</dt>
                  <dd className="text-foreground-800">{movie.actors.join("、")}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-foreground-400 w-16 shrink-0">类型</dt>
                  <dd className="flex flex-wrap gap-2">
                    {movie.genres.map((g) => (
                      <Link
                        key={g}
                        to={`/category/${movie.type}`}
                        className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-800 text-xs hover:bg-secondary-200 transition-colors cursor-pointer"
                      >
                        {g}
                      </Link>
                    ))}
                  </dd>
                </div>
              </dl>

              <div className="mt-6">
                <p className="text-xs text-foreground-400 mb-2">分享（演示功能，暂不支持真实分享）</p>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { label: "微信", icon: "ri-wechat-line" },
                    { label: "微博", icon: "ri-weibo-line" },
                    { label: "复制链接", icon: "ri-link" },
                  ].map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => handleShare(s.label)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-300 text-foreground-600 text-sm hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className={s.icon}></i>
                      {s.label}
                    </button>
                  ))}
                </div>
                {shareMsg && (
                  <p className="mt-3 text-xs text-secondary-800 bg-secondary-100 rounded-md px-3 py-2 inline-block">{shareMsg}</p>
                )}
              </div>
            </div>
          </div>

          {/* 剧情简介 */}
          <section className="mt-10">
            <SectionHeading title="剧情简介" />
            <div className="space-y-4 text-sm md:text-base leading-relaxed text-foreground-700">
              {paragraphs.map((text, i) => (
                <p key={i}>{text}</p>
              ))}
            </div>
          </section>
        </div>

        {/* 侧栏 */}
        <aside>
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 mb-6">
            <h2 className="text-base font-bold text-foreground-950 mb-3">作品信息</h2>
            <ul className="space-y-2.5 text-sm text-foreground-600">
              <li className="flex justify-between"><span className="text-foreground-400">类别</span><span>{movie.type}</span></li>
              <li className="flex justify-between"><span className="text-foreground-400">上映年份</span><span>{movie.year}</span></li>
              <li className="flex justify-between"><span className="text-foreground-400">制片地区</span><span>{movie.country}</span></li>
              <li className="flex justify-between"><span className="text-foreground-400">评分</span><span className="text-accent-600 font-bold">{movie.rating.toFixed(1)}</span></li>
              <li className="flex justify-between gap-4"><span className="text-foreground-400 shrink-0">主要类型</span><span className="text-right">{movie.genres.join(" / ")}</span></li>
            </ul>
          </div>

          <Link
            to={`/category/${movie.type}`}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-md border border-primary-200 text-primary-600 text-sm hover:bg-primary-50 transition-colors cursor-pointer"
          >
            <i className="ri-apps-2-line"></i>
            浏览更多{movie.type}
          </Link>
        </aside>
      </div>

      {/* 相关推荐 */}
      <section className="mt-12">
        <SectionHeading title="相关推荐" subtitle="根据类型与主演为你推荐" />
        {related.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-foreground-500">暂无相关推荐。</p>
        )}
      </section>
    </div>
  );
}